// Kiosk device JWT (S3 — ADR-0009 §5): the long-lived credential the operator
// provisions ONCE on the reception pointeuse tablet. HS256 over the Supabase
// project JWT secret (the guest-chat pattern), claim `device: 'pointeuse'`.
//
// Deliberately NO `role`/`aud` claims: this token is verified only by our own
// /api/staff/clock edge function and must never pass as a Supabase
// authenticated token at PostgREST/Realtime.
//
// Pure WebCrypto (globalThis.crypto) — runs identically on Cloudflare Workers
// and Node 20+. The base64url + HMAC primitives come from
// `@lemontaigu/platform`; until 2026-07-26 this file carried its own byte-identical
// copies (ladder step 2). The platform helpers keep `CryptoKey` out of their
// public signatures, which preserves the constraint that made the copies exist:
// apps/staff-bridge typechecks this package under Node-only types.

import { fromBase64Url, hmacSign, hmacVerify, toBase64Url } from '@lemontaigu/platform';

const encoder = new TextEncoder();

export const KIOSK_DEVICE_CLAIM = 'pointeuse';
/** Default device-token lifetime — re-provision the tablet every ~2 years. */
export const KIOSK_TOKEN_TTL_SECONDS = 2 * 365 * 24 * 3600;

export async function mintKioskToken(
  jwtSecret: string,
  ttlSeconds: number = KIOSK_TOKEN_TTL_SECONDS,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): Promise<string> {
  const header = { alg: 'HS256', typ: 'JWT' };
  const payload = {
    iss: 'lemontaigu-staff',
    device: KIOSK_DEVICE_CLAIM,
    iat: nowSeconds,
    exp: nowSeconds + ttlSeconds,
  };
  const head = toBase64Url(encoder.encode(JSON.stringify(header)));
  const body = toBase64Url(encoder.encode(JSON.stringify(payload)));
  return `${head}.${body}.${await hmacSign(jwtSecret, `${head}.${body}`)}`;
}

/** True only for a well-formed, unexpired token carrying the pointeuse claim. */
export async function verifyKioskToken(
  jwtSecret: string,
  jwt: string,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): Promise<boolean> {
  const parts = jwt.split('.');
  if (parts.length !== 3) return false;
  const [head, body, signature] = parts as [string, string, string];

  if (!(await hmacVerify(jwtSecret, `${head}.${body}`, signature))) return false;

  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(new TextDecoder().decode(fromBase64Url(body))) as Record<string, unknown>;
  } catch {
    return false;
  }
  if (typeof payload.exp !== 'number' || payload.exp <= nowSeconds) return false;
  if (payload.device !== KIOSK_DEVICE_CLAIM) return false;
  // Belt-and-suspenders: a Supabase-shaped token (role claim) is never a
  // device token, even if it somehow validates under the same secret.
  if ('role' in payload) return false;
  return true;
}
